import React from 'react';
import BaseDialog from './BaseDialog';
import Button from './Button';
import { Input } from './FormField';
import { SETTING_CATEGORY_LABELS } from './constants';

/**
 * SettingDialog Component
 * 
 * Unified dialog for adding or editing setting options (complaint types, statuses, priorities, etc.)
 * Follows DRY principle by combining Add and Edit functionality
 * 
 * @param {boolean} isOpen - Controls dialog visibility
 * @param {string} mode - 'add' or 'edit'
 * @param {string} category - Setting category (e.g., 'complaintTypes', 'complaintStatuses')
 * @param {string} value - Current input value
 * @param {function} onValueChange - Callback when value changes
 * @param {function} onSubmit - Callback when user submits the form
 * @param {function} onCancel - Callback when user cancels
 * @param {boolean} loading - Loading state for async operations
 */
const SettingDialog = ({
  isOpen,
  mode = 'add',
  category,
  value,
  onValueChange,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  if (!isOpen) return null;

  const categoryLabel = SETTING_CATEGORY_LABELS[category] || 'Item';
  const title = mode === 'add' ? `Add new ${categoryLabel}` : `Edit ${categoryLabel}`;
  const submitButtonText = mode === 'add' ? 'Add' : 'Update';

  const handleSubmit = () => {
    const trimmedValue = (value || '').trim();
    if (trimmedValue) {
      onSubmit(trimmedValue);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={onCancel} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={loading || !value?.trim()}
      >
        {loading ? `${submitButtonText}ing...` : submitButtonText}
      </Button>
    </>
  );

  return (
    <BaseDialog
      isOpen={isOpen}
      title={title}
      onClose={onCancel}
      footer={footer}
      size="small"
    >
      <Input
        autoFocus
        type="text"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder="Enter value"
        disabled={loading}
      />
    </BaseDialog>
  );
};

export default SettingDialog;

